/**
 * Elastic animation utility functions for AnimatedItem interface
 * Provides elastic entrance and exit animations with spring-like behavior
 */

import { interpolate, Easing } from 'remotion';

/**
 * Calculate elastic animation transform values using elastic easing
 * @param progress - Animation progress (0 to 1)
 * @param direction - Animation direction ('entrance' or 'exit')
 * @param amplitude - Elastic amplitude (default: 1.0)
 * @param period - Elastic period (default: 0.4)
 * @returns Transform values for current animation frame
 */
export function calculateElasticTransform(
  progress: number,
  direction: 'entrance' | 'exit',
  amplitude: number = 1.0,
  period: number = 0.4
): { scale: number; opacity: number } {
  // Validate inputs
  if (progress < 0 || progress > 1) {
    console.warn(`Invalid progress value: ${progress}. Must be between 0 and 1.`);
    progress = Math.max(0, Math.min(1, progress));
  }
  
  if (amplitude <= 0 || period <= 0) {
    console.warn(`Invalid amplitude (${amplitude}) or period (${period}). Must be positive.`);
    amplitude = Math.max(0.1, amplitude);
    period = Math.max(0.1, period);
  }

  // Use Remotion's interpolate for smooth progress transition
  const easedProgress = direction === 'entrance' 
    ? interpolate(progress, [0, 1], [0, 1], {
        easing: Easing.out(Easing.cubic)
      })
    : interpolate(progress, [0, 1], [1, 0], {
        easing: Easing.in(Easing.cubic)
      });
  
  let scale: number;
  
  if (easedProgress === 0) {
    scale = 0;
  } else if (easedProgress === 1) {
    scale = 1;
  } else {
    // Calculate elastic easing
    const s = period / 4;
    const elasticScale = amplitude * Math.pow(2, -10 * easedProgress) * 
      Math.sin((easedProgress - s) * (2 * Math.PI) / period) + 1;
    
    // Ensure minimum scale and reasonable bounds
    scale = Math.max(0.1, Math.min(1.3, elasticScale));
  }
  
  // Fade in during elastic animation
  const opacity = Math.min(1, easedProgress * 1.1);
  
  return { scale, opacity };
}

/**
 * Get CSS transform for elastic animation with hardware acceleration
 * @param scale - Current scale value
 * @param opacity - Current opacity value
 * @returns CSS style object for elastic animation
 */
export function getElasticAnimationStyle(scale: number, opacity: number): React.CSSProperties {
  return {
    transform: `scale3d(${scale}, ${scale}, 1)`,
    opacity,
    willChange: 'transform, opacity',
    transformOrigin: 'center center',
  };
}

/**
 * Calculate elastic animation with default settings
 */
export function calculateElasticAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  const { scale, opacity } = calculateElasticTransform(progress, direction);
  return getElasticAnimationStyle(scale, opacity);
}